/**
 * deviceSelectors.js -- Read-only queries over sysconfig device data.
 *
 * @typedef {import("../types/deviceTypes").Device} Device
 * @typedef {import("../types/deviceTypes").DeviceType} DeviceType
 */

import {
  COUPLING_LINKS,
  ALL_COUPLING_KEYS,
  isCouplableType,
  isCouplablePair,
} from "./deviceCoupling";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

export const DEVICE_COLLECTIONS = [
  { collectionName: "evs", type: "ev" },
  { collectionName: "batteries", type: "battery" },
  { collectionName: "solar_arrays", type: "solar" },
  { collectionName: "fueled_generators", type: "generator" },
  { collectionName: "uncontrollable_loads", type: "consumption" },
  { collectionName: "controllable_loads", type: "controllable_load" },
];

/**
 * @param {Object} sysconfig
 * @returns {Array<{ meterIndex: number, meter: Object, collectionName: string, type: DeviceType, devices: Array<Object> }>}
 */
export function getAllMeterDeviceCollections(sysconfig) {
  const result = [];
  const meters = sysconfig?.Microgrid?.meters || [];

  meters.forEach((meter, meterIndex) => {
    DEVICE_COLLECTIONS.forEach(({ collectionName, type }) => {
      if (Array.isArray(meter[collectionName])) {
        result.push({
          meterIndex,
          meter,
          collectionName,
          type,
          devices: meter[collectionName],
        });
      }
    });
  });

  return result;
}

// ---------------------------------------------------------------------------
// Exported selectors
// ---------------------------------------------------------------------------

/**
 * Find a device by its device_id across all meters and collections.
 * @param {Object} sysconfig
 * @param {string} deviceId
 * @returns {{ meterIndex: number, meter: Object, collectionName: string, type: DeviceType, deviceIndex: number, device: Object }|null}
 */
export function findDeviceById(sysconfig, deviceId) {
  const collections = getAllMeterDeviceCollections(sysconfig);
  for (const c of collections) {
    const index = c.devices.findIndex((dev) => dev.device_id === deviceId);
    if (index !== -1) {
      return {
        meterIndex: c.meterIndex,
        meter: c.meter,
        collectionName: c.collectionName,
        type: c.type,
        deviceIndex: index,
        device: c.devices[index],
      };
    }
  }
  return null;
}

/**
 * Produce a normalized flat list of all devices across all meters.
 * @param {Object} sysconfig
 * @returns {Device[]}
 */
export function listDevicesFromSysconfig(sysconfig) {
  if (!sysconfig) return [];
  const collections = getAllMeterDeviceCollections(sysconfig);
  const devices = [];

  collections.forEach(({ meterIndex, meter, type, devices: devs }) => {
    devs.forEach((dev, idx) => {
      const hasDeviceId = Boolean(dev.device_id);
      const id = dev.device_id || `${type}-${meterIndex}-${idx}`;
      devices.push({
        device_id: id,
        type,
        name: dev.name || dev.device_name || id,
        meterName: meter.name || meter.meter_name || String(meterIndex),
        meterIndex,
        hasDeviceId,
        raw: dev,
      });
    });
  });

  return devices;
}

// ---------------------------------------------------------------------------
// Coupling queries
// ---------------------------------------------------------------------------

/**
 * Every couplable device on one meter, by id, with its type and name.
 * Devices without a device_id are left out: a coupling is a pair of ids and
 * there is nothing to point at.
 * @param {Object} meter
 * @returns {Map<string, { device_id: string, type: string, name: string, raw: Object }>}
 */
export function couplableDevicesOnMeter(meter) {
  const byId = new Map();
  DEVICE_COLLECTIONS.forEach(({ collectionName, type }) => {
    if (!isCouplableType(type)) return;
    (meter?.[collectionName] || []).forEach((dev) => {
      if (!dev?.device_id) return;
      byId.set(dev.device_id, {
        device_id: dev.device_id,
        type,
        name: dev.name || dev.device_name || dev.device_id,
        raw: dev,
      });
    });
  });
  return byId;
}

/**
 * The ids a device is coupled to, read from BOTH sides of the link: its own
 * coupling keys and every other device on its meter that names it. Either
 * side alone is a coupling to the engine, so a sysconfig that only carries
 * one side (written by hand, or before the mutations wrote both) still
 * reports the link. Only supported pairs on the same meter are returned;
 * ids that name a missing device, an unsupported kind or the device itself
 * are dropped.
 *
 * @param {Object} sysconfig
 * @param {string} deviceId
 * @returns {string[]} partner device ids, in the order first seen
 */
export function getCoupledDeviceIds(sysconfig, deviceId) {
  const loc = findDeviceById(sysconfig, deviceId);
  if (!loc || !isCouplableType(loc.type)) return [];

  const devices = couplableDevicesOnMeter(loc.meter);
  const me = devices.get(deviceId);
  if (!me) return [];

  const partners = [];
  const seen = new Set([deviceId]);
  const take = (id) => {
    const other = devices.get(id);
    if (!other || seen.has(id) || !isCouplablePair(me.type, other.type)) return;
    seen.add(id);
    partners.push(id);
  };

  COUPLING_LINKS[me.type].forEach(({ key }) => {
    (me.raw[key] || []).forEach(take);
  });

  devices.forEach((other) => {
    if (other.device_id === deviceId) return;
    const names = ALL_COUPLING_KEYS.some((key) =>
      (other.raw[key] || []).includes(deviceId),
    );
    if (names) take(other.device_id);
  });

  return partners;
}

/**
 * The devices a device of `deviceType` could be coupled to on a meter: every
 * device of a couplable kind on that meter other than itself, in table order.
 *
 * @param {Object} sysconfig
 * @param {{ deviceType: string, deviceId?: string|null, meterIndex?: number }} target
 *   deviceId is null for a device being added; meterIndex defaults to the
 *   meter the device lives on, or the first meter for a new device.
 * @returns {Array<{ device_id: string, type: string, name: string }>}
 */
export function listCouplingCandidates(sysconfig, { deviceType, deviceId = null, meterIndex } = {}) {
  if (!isCouplableType(deviceType)) return [];
  const meters = sysconfig?.Microgrid?.meters || [];
  let index = meterIndex;
  if (index == null) {
    const loc = deviceId ? findDeviceById(sysconfig, deviceId) : null;
    index = loc ? loc.meterIndex : 0;
  }
  const meter = meters[index];
  if (!meter) return [];

  const candidates = [];
  couplableDevicesOnMeter(meter).forEach((dev) => {
    if (dev.device_id === deviceId) return;
    if (!isCouplablePair(deviceType, dev.type)) return;
    candidates.push({ device_id: dev.device_id, type: dev.type, name: dev.name });
  });
  return candidates;
}
